import { Check, Sparkles, SprayCan, ArrowRightLeft, BedDouble, Hammer } from "lucide-react";
import { useQuote } from "../lib/quoteContext";
import { SERVICES } from "../lib/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const ICONS = { Sparkles, SprayCan, ArrowRightLeft, BedDouble, Hammer };

function ServiceCard({ s }) {
  const Icon = ICONS[s.icon] || Sparkles;
  const { openQuote } = useQuote();
  return (
    <div
      className="bg-[#DCEBFB] rounded-[20px] p-6 lg:p-7 flex flex-col h-full transition-transform hover:-translate-y-1 hover:shadow-lg"
      data-testid={`service-card-${s.id}`}
    >
      <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center mb-5 shadow-sm">
        <Icon className="w-5 h-5 text-[#173B86]" />
      </div>
      <h3 className="font-serif text-[22px] lg:text-[24px] text-[#1A2B4A] mb-4 leading-tight">
        {s.title}
      </h3>
      <ul className="space-y-2.5 mb-6 flex-1">
        {s.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-[#1A2B4A]/85 leading-snug">
            <Check className="w-4 h-4 text-[#173B86] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => openQuote(s.title)}
        className="tc-btn-primary w-full mt-auto"
        data-testid={`service-cta-${s.id}`}
      >
        Get a Quote
      </button>
    </div>
  );
}

export default function Services() {
  return (
    <section id="services" className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="services-section">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10">
        <p className="tc-eyebrow mb-3">Our Services</p>
        <h2 className="font-serif text-[34px] sm:text-[44px] lg:text-[52px] text-[#173B86] leading-[1.05] tracking-[-0.01em] max-w-[820px]">
          Cleaning, Tailored to You
        </h2>
        <p className="text-[14.5px] sm:text-[15px] text-[#5b6a85] mt-3 max-w-[700px]">
          From weekly upkeep to deep transformations — choose the service that
          fits your life.
        </p>

        {/* Desktop / tablet grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mt-12">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>

        {/* Mobile accordion (per design) */}
        <div className="md:hidden mt-8">
          <Accordion type="single" collapsible className="space-y-3" data-testid="services-accordion">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Sparkles;
              return (
                <AccordionItem
                  key={s.id}
                  value={s.id}
                  className="bg-[#DCEBFB] rounded-[18px] border-none px-5"
                >
                  <AccordionTrigger className="hover:no-underline py-4 text-[15px] font-serif text-[#1A2B4A] [&>svg]:text-[#173B86]">
                    <span className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-[#173B86]" />
                      {s.title}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <ul className="space-y-2 mb-4">
                      {s.items.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-[#1A2B4A]/85">
                          <Check className="w-3.5 h-3.5 text-[#173B86] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    <ServiceMobileCTA service={s.title} id={s.id} />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ServiceMobileCTA({ service, id }) {
  const { openQuote } = useQuote();
  return (
    <button
      onClick={() => openQuote(service)}
      className="tc-btn-primary w-full"
      data-testid={`service-cta-mobile-${id}`}
    >
      Request a Quote
    </button>
  );
}
